'use client';

/**
 * Invite Team Member Modal
 *
 * Modal dialog for inviting a new team member by email with a role selection
 * (member or admin). Shows inline validation, async submission state, and a
 * success confirmation before auto-closing. Built on the shared Dialog shell
 * using the same layout pattern as rename-chat-dialog.
 */

import { Check, ChevronDown, Loader2, Mail, X } from 'lucide-react';
import { createLogger } from '@ctrl-enter/tools/utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAdminInviteMemberMutation } from '../hooks/use-gateway-queries';
import { cn } from '../utils/cn';
import { Dialog } from './dialog';
import { Button } from './button';

const logger = createLogger('web:component:invite-team-member-modal');

export type InviteTeamMemberModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const InviteTeamMemberModal: React.FC<InviteTeamMemberModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inviteMutation = useAdminInviteMemberMutation();

  // Reset form state and focus the email input whenever the dialog opens.
  // The original inline dialog was conditionally mounted, so this preserves
  // that fresh-start behavior now that the component stays mounted and
  // toggles via `isOpen`.
  useEffect(() => {
    if (!isOpen) return;
    setEmail('');
    setRole('member');
    setError(null);
    setSuccess(false);
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleInvite = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!email.trim()) {
        setError('Email is required');
        return;
      }
      setError(null);
      try {
        await inviteMutation.mutateAsync({ body: { email: email.trim(), role } });
        setSuccess(true);
        setTimeout(onClose, 1200);
      } catch (err: unknown) {
        const errAny = err as { body?: { message?: string }; message?: string };
        const msg: string = errAny?.body?.message ?? errAny?.message ?? 'Failed to send invitation';
        setError(msg);
        logger.error('[InviteTeamMemberModal] Invite failed:', err);
      }
    },
    [email, role, inviteMutation, onClose]
  );

  const isSubmitting = inviteMutation.isPending;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      disableClose={isSubmitting}
      aria-labelledby="invite-team-member-dialog-title"
      className="p-6"
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        disabled={isSubmitting}
        className={cn(
          'absolute top-4 right-4 p-1.5 rounded-md',
          'text-muted-foreground hover:text-foreground',
          'hover:bg-accent transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
        aria-label="Close dialog"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Icon */}
      <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10">
        <Mail className="w-5 h-5 text-primary" />
      </div>

      {/* Title */}
      <h2
        id="invite-team-member-dialog-title"
        className="text-lg font-semibold text-center text-foreground mb-4"
      >
        Invite Team Member
      </h2>

      {/* Form */}
      <form onSubmit={handleInvite}>
        <div className="mb-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              Email address
            </label>
            <input
              ref={inputRef}
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setError(null);
              }}
              disabled={isSubmitting}
              placeholder="colleague@example.com"
              className={cn(
                'w-full px-3 py-2 rounded-lg text-sm bg-bg-neutral-8 border text-foreground',
                'focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground/50',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                error ? 'border-destructive' : 'border-border'
              )}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Role</label>
            <div className="relative">
              <select
                value={role}
                onChange={e => setRole(e.target.value as 'member' | 'admin')}
                disabled={isSubmitting}
                className="w-full appearance-none px-3 py-2 rounded-lg text-sm bg-bg-neutral-8 border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <ChevronDown
                size={12}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
            </div>
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        {/* Actions */}
        <div className="flex gap-3 w-full justify-between">
          <Button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            variant="secondary"
            size="md"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || success} variant="primary" size="md">
            {success ? (
              <>
                <Check className="w-4 h-4 mr-1.5" />
                Sent!
              </>
            ) : isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-1.5" />
                Send invite
              </>
            )}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
